'use client'

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@mui/material'

import { ArrowRightIcon } from '@/src/components/ui/ArrowIcons'

interface RecoverDraftDialogsProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  titleOverride?: string
  openRecoverEmail: boolean
  onOpenRecoverEmailChange: (_open: boolean) => void
  email: string
  setEmail: (_email: string) => void
  code: string
  setCode: (_code: string) => void
  recoverEmail: string
  setRecoverEmail: (_email: string) => void
  loading: boolean
  loadingRecover: boolean
  error: string | null
  draftLoaded: boolean
  recoverSuccess: boolean
  handleFetchDraft: () => void
  handleRequestCode: () => void
}

export const RecoverDraftDialogs: React.FC<RecoverDraftDialogsProps> = ({
  open,
  onOpenChange,
  titleOverride,
  openRecoverEmail,
  onOpenRecoverEmailChange,
  email,
  setEmail,
  code,
  setCode,
  recoverEmail,
  setRecoverEmail,
  loading,
  loadingRecover,
  error,
  draftLoaded,
  recoverSuccess,
  handleFetchDraft,
  handleRequestCode
}) => {
  const { t } = useTranslation()

  const handleMainDialogClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
    onOpenChange(false)
  }

  const handleRequestCodeDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
    onOpenRecoverEmailChange(false)
  }

  useEffect(() => {
    if (recoverSuccess) {
      const timer = setTimeout(() => {
        onOpenRecoverEmailChange(false)
        onOpenChange(true)
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [recoverSuccess, onOpenChange, onOpenRecoverEmailChange])

  return (
    <>
      {/* Main draft recovery dialog */}
      <Dialog
        open={open}
        onClose={handleMainDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {titleOverride ?? t('recover.title')}
          <IconButton
            aria-label="close"
            onClick={() => onOpenChange(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography
              variant="caption"
              component="div"
              dangerouslySetInnerHTML={{ __html: t('recover.description') }}
            />
            <TextField
              label={t('recover.emailLabel')}
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              variant="standard"
            />
            <TextField
              label={t('recover.codeLabel')}
              type="text"
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={loading}
              variant="standard"
            />

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {draftLoaded && <Alert severity="success">{t('recover.draftLoaded')}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              onOpenChange(false)
              onOpenRecoverEmailChange(true)
            }}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            {t('recover.fetchCode')}
          </Button>
          <Button
            onClick={handleFetchDraft}
            variant="contained"
            disabled={loading}
            sx={{ flex: 1, justifyContent: 'space-between' }}
            endIcon={<ArrowRightIcon />}
          >
            {t('recover.fetchCta')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Code request dialog */}
      <Dialog
        open={openRecoverEmail}
        onClose={handleRequestCodeDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t('recover.requestCodeTitle')}
          <IconButton
            aria-label="close"
            onClick={() => onOpenRecoverEmailChange(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('recover.requestCodeDescription')}
            </Typography>
            <TextField
              label={t('recover.emailLabel')}
              type="email"
              fullWidth
              value={recoverEmail}
              onChange={(e) => setRecoverEmail(e.target.value)}
              disabled={loadingRecover}
              variant="standard"
            />
            {loadingRecover && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {recoverSuccess && <Alert severity="success">{t('recover.codeSentSuccess')}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
            onClick={() => onOpenRecoverEmailChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRequestCode}
            variant="contained"
            disabled={loadingRecover}
            sx={{ flex: 1, justifyContent: 'space-between' }}
            endIcon={<ArrowRightIcon />}
          >
            {t('recover.requestCodeCta')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
